import React, {
	Fragment,
	useState,
	useEffect,
	useRef,
	useCallback,
} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useSelector } from 'react-redux';
import { compose } from '@reduxjs/toolkit';
import withRouter from 'lib/withRouter';
import MenuItem from 'components/brand/Header/MenuItem';
import DealsLink from 'components/brand/Header/DealsLink';
import { Hamburger, MenuCloseIcon } from 'components/svg';
import withCollapsibleMenuHandlers from 'components/brand/Header/withCollapsibleMenuHandlers';
const buildDrilldownLink = (cityDrilldownData) => {
	if (!cityDrilldownData || !cityDrilldownData.content) {
		return null;
	}

	const { content, city, state } = cityDrilldownData;
	const sections = [];
	const { amenities, unitTypes, vehicleStorage } = content;

	if (Array.isArray(amenities) && amenities.length) {
		sections.push({
			sectionTitle: 'AMENITY',
			links: amenities,
		});
	}

	if (Array.isArray(unitTypes) && unitTypes.length) {
		sections.push({
			sectionTitle: 'SIZE',
			links: unitTypes,
		});
	}

	if (Array.isArray(vehicleStorage) && vehicleStorage.length) {
		sections.push({
			sectionTitle: 'VEHICLE',
			links: vehicleStorage,
		});
	}

	if (!sections.length) {
		return null;
	}

	const locationLabel = [city, state].filter(Boolean).join(', ');

	return {
		text: 'Find Storage',
		dropdownHeader: locationLabel
			? `Find Storage Units in ${locationLabel}`
			: undefined,
		innerLinks: sections,
		isActive: false,
	};
};

const getLinksData = (cityDrilldownData) => {
	const customLink = buildDrilldownLink(cityDrilldownData);
	const links = [
		{
			text: 'Find Storage',
			innerLinks: [
				{
					sectionTitle: 'AMENITY',
					links: [
						{
							href: `/near-me/24-hour-storage.html`,
							text: `24 Hour Storage Near You`,
						},
						{
							href: `/near-me/climate-controlled-storage.html`,
							text: `Climate Controlled Storage Near You`,
						},
						{
							href: `/near-me/drive-up-storage.html`,
							text: `Drive Up Storage Near You`,
						},
						{
							href: `/near-me/storage-with-electricity.html`,
							text: `Storage With Electricity Near You`,
						},
						{
							href: `/near-me/storage-with-free-truck-rental.html`,
							text: `Storage With Free Truck Rental Near You`,
						},
						{
							href: `/near-me/wine-storage.html`,
							text: `Wine Storage Near You`,
						},
					],
				},
				{
					sectionTitle: 'SIZE',
					links: [
						{
							href: `/near-me/mini-storage.html`,
							text: `Mini Storage Near You`,
						},
						{
							href: `/near-me/5x5-storage.html`,
							text: `5x5 Storage Near You`,
						},
						{
							href: `/near-me/5x10-storage.html`,
							text: `5x10 Storage Near You`,
						},
						{
							href: `/near-me/5x15-storage.html`,
							text: `5x15 Storage Near You`,
						},
						{
							href: `/near-me/10x10-storage.html`,
							text: `10x10 Storage Near You`,
						},
						{
							href: `/near-me/10x15-storage.html`,
							text: `10x15 Storage Near You`,
						},
						{
							href: `/near-me/10x20-storage.html`,
							text: `10x20 Storage Near You`,
						},
						{
							href: `/near-me/10x25-storage.html`,
							text: `10x25 Storage Near You`,
						},
						{
							href: `/near-me/10x30-storage.html`,
							text: `10x30 Storage Near You`,
						},
						{
							href: `/near-me/locker-storage.html`,
							text: `Storage Lockers Near You`,
						},
					],
				},
				{
					sectionTitle: 'VEHICLE',
					links: [
						{
							href: `/near-me/boat-storage.html`,
							text: `Boat Storage Near You`,
						},
						{
							href: `/near-me/car-storage.html`,
							text: `Car Storage Near You`,
						},
						{
							href: `/near-me/rv-storage.html`,
							text: `RV Storage Near You`,
						},
					],
				},
			],
			isActive: false,
		},
		{
			text: 'Storage Types',
			innerLinks: [
				{ href: '/car-storage.html', text: 'Car Storage' },
				{ href: '/rv-storage.html', text: 'RV Storage' },
				{ href: '/boat-storage.html', text: 'Boat Storage' },
				{ href: '/college-storage.html ', text: 'College Storage' },
				{ href: '/portable-storage.html', text: 'Portable Storage' },
				{ href: '/business-storage.html', text: 'Business Storage' },
				{
					href: '/climate-controlled-storage.html',
					text: 'Climate Controlled',
				},
				{ href: '/storage-101.html', text: 'Find Self-Storage' },
			],
			isActive: false,
		},
		{
			text: 'Storage 101',
			innerLinks: [
				{
					href: 'https://storageguide.sparefoot.com/self-storage-amenities/ ',
					text: 'Storage Unit Amenities',
				},
				{
					href: 'https://storageguide.sparefoot.com/the-basics/ ',
					text: 'Storage FAQ',
				},
				{
					href: 'https://storageguide.sparefoot.com/storage-unit-safety-and-security/  ',
					text: 'Storage Security',
				},
				{
					href: '/storage-unit-size-guide.html ',
					text: 'Storage Unit Size Guide',
				},
				{
					href: 'https://www.sparefoot.com/blog/category/industry-trends/',
					text: 'Storage Beat',
				},
			],
			isActive: false,
		},
		{
			text: 'Learn',
			innerLinks: [
				{
					href: 'https://www.sparefoot.com/blog/category/general-storage-advice/',
					text: 'General Storage Advice',
				},
				{
					href: 'https://www.sparefoot.com/blog/category/how-to-store/',
					text: 'How to Store It',
				},
				{
					href: 'https://www.sparefoot.com/blog/category/moving-tips/',
					text: 'Moving Tips',
				},
				{
					href: 'https://www.sparefoot.com/blog/category/getting-organized/',
					text: 'Getting Organized',
				},
				{
					href: 'https://www.sparefoot.com/blog/category/city-guides/',
					text: 'City Guides',
				},
				{
					href: 'https://www.sparefoot.com/blog/category/self-storage/',
					text: 'Self Storage',
				},
				{
					href: 'https://www.sparefoot.com/blog/category/industry-trends/news/',
					text: 'News',
				},
			],
			isActive: false,
		},
		{ href: 'https://storageguide.sparefoot.com/', text: 'Help Center' },
		{ href: 'https://about.sparefoot.com/ ', text: 'About Us' },
		{
			customItem: <DealsLink className="menu-secondary" />,
		},
		{
			href: 'https://marketing.sparefoot.com',
			text: 'Add Your Facility'
		},
	];

	return customLink ? [customLink, ...links.slice(1)] : links;
};

export const enhance = compose(withRouter, withCollapsibleMenuHandlers);
export function HeaderMenu({
	menuExpanded,
	location,
	hideDealsLink,
}) {
	const drilldownContent = useSelector(
		(state) =>
			state?.search?.drilldownLinks || state?.facilities?.drillDownLinks,
	);
	const drilldownCity = useSelector((state) => state.search.location.city);
	const drilldownRegion = useSelector(
		(state) => state.search.location.region,
	);

	const menuToggleButtonRef = useRef(null);
	const linkContainerRef = useRef(null);
	const menuToggleInputRef = useRef(null);

	const buildLinksFromProps = useCallback(() => {
		const pathname = location?.pathname || '';
		const isNearMePage = pathname.includes('/near-me/');
		const cityDrilldownLinks = isNearMePage
			? null
			: {
					content: drilldownContent,
					city: drilldownCity,
					state: drilldownRegion,
				};
		return getLinksData(cityDrilldownLinks);
	}, [
		drilldownContent,
		drilldownCity,
		drilldownRegion,
		location,
	]);

	const [links, setLinks] = useState(() => buildLinksFromProps());

	useEffect(() => {
		setLinks(buildLinksFromProps());
	}, [buildLinksFromProps]);

	useEffect(() => {
		const handleClickOutside = (event) => {
			const element = menuToggleInputRef?.current;
			const menuToggleButton = menuToggleButtonRef?.current;
			const linkContainer = linkContainerRef?.current;

			const isOutsideMenu =
				!menuToggleButton?.contains(event.target) &&
				!linkContainer?.contains(event.target);

			if (isOutsideMenu && element?.checked) {
				element.checked = false;
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	const handleClick = useCallback((text) => {
		setLinks((prevLinks) =>
			prevLinks.map((link) => {
				if (link?.text && text.includes(link?.text)) {
					return { ...link, isActive: !link.isActive };
				}
				return { ...link, isActive: false };
			}),
		);
	}, []);

	return (
		<nav
			className={classnames('header-menu', {
				expanded: menuExpanded,
			})}
		>
			<div
				className="menu-toggle-button"
				ref={menuToggleButtonRef}
			>
				<label htmlFor="mobile-menu-toggle">
					<span className="hidden-text">Show Menu</span>
					<input
						ref={menuToggleInputRef}
						name="mobile-menu-toggle"
						type="checkbox"
						id="mobile-menu-toggle"
						className="visually-hidden"
					/>
					<Hamburger className="menu-closed-icon" />
					<MenuCloseIcon className="menu-open-icon" />
				</label>
			</div>
			<div
				className="link-container"
				ref={linkContainerRef}
			>
				{links.map((link) =>
					link.customItem ? (
						hideDealsLink && link.customItem.type === DealsLink ? null : (
							<Fragment key={link.customItem}>
								{link.customItem}
							</Fragment>
						)
					) : (
						<MenuItem
							{...link}
							key={link.text}
							id={link.text?.toLowerCase().replace(/\s+/g, '-')}
							handleClick={handleClick}
						/>
					),
				)}
			</div>
		</nav>
	);
}
HeaderMenu.propTypes = {
	menuExpanded: PropTypes.bool,
	onClickOutside: PropTypes.func,
	toggleMenu: PropTypes.func,
	viewportWidth: PropTypes.number,
	geolocation: PropTypes.shape({
		city: PropTypes.string,
		region: PropTypes.string,
	}),
	drilldownContent: PropTypes.object,
	drilldownCity: PropTypes.string,
	drilldownRegion: PropTypes.string,
	location: PropTypes.shape({
		pathname: PropTypes.string,
	}),
	history: PropTypes.shape({
		listen: PropTypes.func,
	}),
	hideDealsLink: PropTypes.bool,
};
export default enhance(HeaderMenu);
